import clsx from "clsx"
import { ElementCardAssets } from "@/constants/cardAssets"
import { CARD_SIZES } from "@/constants/guardians"
import {
  BattleCardBreakdown,
  BattleCardView,
  GameCard,
} from "@/types/battle"
import GuardianCardFace, { getCardCombatPower } from "./GuardianCardFace"
import { computeCardShapeLayout } from "./cardShapeLayout"
import { getCardDimensions } from "./styles"
import type { FooterMetaMode } from "./CardFooterMeta"

export interface GuardianCardProps {
  card?: BattleCardView | GameCard
  size?: keyof typeof CARD_SIZES
  faceDown?: boolean
  showBreakdown?: BattleCardBreakdown
  assets?: ElementCardAssets
  onClick?: () => void
  selected?: boolean
  isActive?: boolean
  winner?: boolean | null
  loading?: boolean
  hideFooterMeta?: boolean
  footerMeta?: FooterMetaMode
  widthOverride?: number
  heroReplayKey?: string | number
  shardScale?: number
  className?: string
}

export default function GuardianCard({
  card,
  size = "md",
  faceDown = false,
  showBreakdown,
  assets,
  onClick,
  selected = false,
  isActive = false,
  winner = null,
  loading = false,
  hideFooterMeta = false,
  footerMeta = "full",
  widthOverride,
  heroReplayKey,
  shardScale,
  className,
}: GuardianCardProps) {
  const base = getCardDimensions(size)
  const width = widthOverride ?? base.width
  const height = widthOverride != null ? width * 1.45 : base.height
  const cardRadius = computeCardShapeLayout(width, height).cardRadius

  if (loading) {
    return (
      <div
        className={clsx("animate-pulse rounded-2xl bg-white/10", className)}
        style={{ width, height }}
      />
    )
  }

  if (faceDown || !card) {
    return (
      <div
        className={clsx(
          "flex items-center justify-center bg-[#0d0d18] text-xs font-bold tracking-widest text-white/80",
          className
        )}
        style={{ width, height, borderRadius: cardRadius }}
      >
        ХРАНИТЕЛИ
      </div>
    )
  }

  const combatPower = getCardCombatPower(card)

  const face = (
    <GuardianCardFace
      card={card}
      width={width}
      height={height}
      combatPower={combatPower}
      assets={assets}
      showBreakdown={showBreakdown}
      hideFooterMeta={hideFooterMeta}
      footerMeta={footerMeta}
      heroReplayKey={heroReplayKey}
      shardScale={shardScale}
    />
  )

  const body = (
    <div
      className={clsx(
        "relative transition-transform",
        selected && "scale-105 ring-2 ring-guardians-accent",
        winner === false && "opacity-50",
        winner === true && "scale-110",
        isActive && "ring-2 ring-guardians-accent",
        className
      )}
      style={{ width, height, borderRadius: cardRadius }}
    >
      {face}
      {isActive ? (
        <div className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-lg bg-guardians-accent px-2 py-1 text-[9px] font-extrabold tracking-wide text-guardians-bg">
          ★ АКТИВНАЯ
        </div>
      ) : null}
    </div>
  )

  if (onClick && !showBreakdown) {
    return (
      <button type="button" onClick={onClick} className="block text-left">
        {body}
      </button>
    )
  }

  return body
}
